import { Injectable } from '@angular/core';
import { NotificationsService } from '@app/core/notifications/notifications.service';
import { Connection } from '@app/modules/businesses/models/accounting.interface';
import { KnowledgeUploadProgress } from '@app/modules/knowledge/models/knowledge.interface';
import { CopilotProgress } from '@app/shared/components/copilot/models/copilot.interface';
import { TaskInstanceExtended, TaskMessage, User } from '@app/shared/interfaces';
import { environment } from '@env/environment';
import { Logger } from '@utils';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

declare const Pusher: any;

export enum PresenceType {
  // AI states
  InboxAIDecidingHowToProceed = 'ai_deciding_how_to_proceed',
  InboxAIWritingAutoReply = 'ai_writing_auto_reply',
  InboxAIAssessingAutoReply = 'ai_assessing_auto_reply',
  InboxAIWritingAutoReplyFailed = 'ai_writing_auto_reply_failed',
  InboxAIWritingAutoReplySucceeded = 'ai_writing_auto_reply_succeeded',
  InboxAIWritingAutoReplyAborted = 'ai_writing_auto_reply_aborted',
  InboxAIWritingAutoReplyPrepared = 'ai_writing_auto_reply_prepared',
  InboxAIGeneratingThreadSummary = 'ai_generating_thread_summary',
  InboxAIGeneratingThreadSummarySucceeded = 'ai_generating_thread_summary_succeeded',
  InboxAIGeneratingThreadSummaryFailed = 'ai_generating_thread_summary_failed',

  // User presence states
  InboxViewingThreadList = 'viewing_thread_list',
  InboxViewingThread = 'viewing_thread',
  InboxTypingMessage = 'typing_message',
  Idle = 'idle',
}

export enum PresenceResourceType {
  Conversations = 'conversations',
}

export interface PresenceInfo {
  resource_id: string | null;
  resource_type: PresenceResourceType;
  type: PresenceType;
  is_ai: boolean;
  user_id: string; // this will also be set for AI
  first_name: string | null;
  last_name: string | null;
  avatar: string | null;
  metadata: {
    [key: string]: any;
  } | null;
  created_at: Date | string;
}

export interface InboxUpdatePusherPayload {
  uuid: string;
}

@Injectable({
  providedIn: 'root',
})
export class PusherService {
  private pusher: any;

  // Pusher untyped channels
  private channel_global: any;
  private channel_user: any;
  private channel_team: any;
  private channelNames = {
    global: '',
    user: '',
    team: '',
  };

  private userIdHash: string;
  private userObj: User;

  private _inboxListUpdate: Subject<InboxUpdatePusherPayload> = new Subject();
  public inboxListUpdate = this._inboxListUpdate.asObservable();

  private _knowledgeHubImportProgress: Subject<KnowledgeUploadProgress> = new Subject();
  public knowledgeHubImportProgress = this._knowledgeHubImportProgress.asObservable();

  private _knowledgeHubChanged: Subject<{ property_id: number }> = new Subject();
  public knowledgeHubChanged = this._knowledgeHubChanged.asObservable();

  private _listingsUpdate: ReplaySubject<any> = new ReplaySubject();
  public listingsUpdate = this._listingsUpdate.asObservable();

  private _onboardingUpdate: ReplaySubject<any> = new ReplaySubject();
  public onboardingUpdate = this._onboardingUpdate.asObservable();

  private _bookingComUpdate: Subject<any> = new Subject();
  public bookingComUpdate$ = this._bookingComUpdate.asObservable();

  // When we're connecting an Airbnb PMS account, we want to know an account has connected or failed to connect
  // so that we can show the connection status
  private _airbnbConnectionStatusUpdate: Subject<any> = new Subject();
  public airbnbConnectionStatusUpdate$ = this._airbnbConnectionStatusUpdate.asObservable();

  // When we're connecting a Vrbo Official account, we want to know the onboarding status changes
  private _vrboOfficialConnectionUpdate: Subject<any> = new Subject();
  public vrboOfficialConnectionUpdate$ = this._vrboOfficialConnectionUpdate.asObservable();

  private _presenceUpdate: Subject<PresenceInfo> = new Subject();
  public presenceUpdate$ = this._presenceUpdate.asObservable();

  private _accountingConnectionUpdate: Subject<{
    type: 'failed_onboarding' | 'finished_onboarding' | 'refreshed' | 'initial_sync_complete';
    connection: Connection;
  }> = new Subject();
  public accountingConnectionUpdate$ = this._accountingConnectionUpdate.asObservable();

  private _subscriptionUpdate: Subject<void> = new Subject();
  public subscriptionUpdate$ = this._subscriptionUpdate.asObservable();

  private _directSitePreviewUpdate: Subject<{ site_id: string }> = new Subject();
  public directSitePreviewUpdate$ = this._directSitePreviewUpdate.asObservable();

  private _copilotProgress: Subject<CopilotProgress> = new Subject();
  public copilotProgress = this._copilotProgress.asObservable();

  private _dynamicPricingRecommendationsUpdate: Subject<any> = new Subject();
  public dynamicPricingRecommendationsUpdate$ = this._dynamicPricingRecommendationsUpdate.asObservable();

  private _taskUpdate: Subject<TaskInstanceExtended> = new Subject();
  public taskUpdate$ = this._taskUpdate.asObservable();
  private _taskMessageUpdate: Subject<TaskMessage> = new Subject();

  private _segmentUpdate: Subject<any> = new Subject();
  public segmentUpdate$ = this._segmentUpdate.asObservable();
  public taskMessageUpdate$ = this._taskMessageUpdate.asObservable();

  private _reservationUpsellsUpdate: Subject<any> = new Subject();
  public reservationUpsellsUpdate$ = this._reservationUpsellsUpdate.asObservable();

  constructor(private notificationsService: NotificationsService) {}

  public init() {
    Logger.info(`Subscribing to Pusher`);

    this.userObj = JSON.parse(localStorage.getItem('user'));
    this.userIdHash = this.userObj.id_hash;

    this.channelNames = {
      global: 'users',
      user: `users.${this.userIdHash}`,
      team: `teams.${this.userObj.team.id}`,
    };

    this.pusher = new Pusher(environment.pusher.key, {
      cluster: environment.pusher.cluster,
      encrypted: true,
    });

    // Subscribe to all our channels
    Object.entries(this.channelNames).forEach((entry) => {
      const [key, value] = entry;

      this[`channel_${key}`] = this.pusher.subscribe(value);
    });

    this.registerForPresenceUpdates();
    this.registerForNotificationNew();
    this.registerForNotificationUpdate();
    this.registerForNotificationDelete();
    this.registerForInboxListUpdates();
    this.registerForKnowledgeBaseUpdates();
    this.registerForListingReviewUpdates();
    this.registerForOnboardingUpdates();
    this.registerForBookingComUpdates();
    this.registerForAirbnbConnectionStatusUpdates();
    this.registerForVrboOfficialConnectionUpdates();
    this.registerForAccountingConnectionUpdates();
    this.registerForCopilotUpdates();
    this.registerForDynamicPricingRecommendationsUpdates();
    this.registerForSubscriptionUpdates();
    this.registerForTaskUpdates();
    this.registerForUpsellsUpdates();
  }

  public closeDown() {
    if (this.pusher) {
      // Subscribe to all our channels
      Object.entries(this.channelNames).forEach((entry) => {
        const [key, value] = entry;

        this.pusher.unsubscribe(value);
      });
    }
  }

  subscribeToSite(siteId: string) {
    const channel = `site.${siteId}`;
    const sub = this.pusher.subscribe(channel);

    sub.bind('site.preview.completed', (data: { site_id: string }) => this._directSitePreviewUpdate.next(data));
  }

  unsubscribeFromSite(siteId: string) {
    if (!siteId) return;
    const channel = `site.${siteId}`;
    this.pusher.unsubscribe(channel);
  }

  private registerForPresenceUpdates() {
    if (this.channel_team) {
      this.channel_team.bind('presence', (data: PresenceInfo) => {
        // Logger.log(`Pusher service: Presence update event received. ${JSON.stringify(data)}`);
        this._presenceUpdate.next(data);
      });
    }
  }

  private registerForAirbnbConnectionStatusUpdates() {
    if (this.channel_user) {
      this.channel_user.bind('channel.airbnb.connected', (data: unknown) => {
        this._airbnbConnectionStatusUpdate.next({ connectionSuccess: true, data });
        Logger.log(`Pusher service: Airbnb connection status update event received. ${JSON.stringify(data)}`);
      });

      this.channel_user.bind('channel.airbnb.error', (data: unknown) => {
        this._airbnbConnectionStatusUpdate.next({ connectionSuccess: false, data });
        Logger.log(`Pusher service: Airbnb connection status error event received. ${JSON.stringify(data)}`);
      });
    }
  }

  private registerForVrboOfficialConnectionUpdates() {
    if (this.channel_user) {
      this.channel_user.bind('channel.vrbo-official.signup_completed', (data: unknown) => {
        this._vrboOfficialConnectionUpdate.next({ event: 'signup_completed', data });
        Logger.log(`Pusher service: Vrbo Official signup completed event received. ${JSON.stringify(data)}`);
      });

      this.channel_user.bind('channel.vrbo-official.properties_imported', (data: unknown) => {
        this._vrboOfficialConnectionUpdate.next({ event: 'properties_imported', data });
        Logger.log(`Pusher service: Vrbo Official properties imported event received. ${JSON.stringify(data)}`);
      });

      this.channel_user.bind('channel.vrbo-official.connection_error', (data: unknown) => {
        this._vrboOfficialConnectionUpdate.next({ event: 'connection_error', data });
        Logger.log(`Pusher service: Vrbo Official connection error event received. ${JSON.stringify(data)}`);
      });

      this.channel_user.bind('channel.vrbo-official.reservations_imported', (data: unknown) => {
        this._vrboOfficialConnectionUpdate.next({ event: 'reservations_imported', data });
        Logger.log(`Pusher service: Vrbo Official reservations imported event received. ${JSON.stringify(data)}`);
      });

      this.channel_user.bind('channel.vrbo-official.pending_confirmation', (data: unknown) => {
        this._vrboOfficialConnectionUpdate.next({ event: 'pending_confirmation', data });
        Logger.log(`Pusher service: Vrbo Official pending confirmation event received. ${JSON.stringify(data)}`);
      });
    }
  }

  private registerForInboxListUpdates() {
    if (this.channel_team) {
      this.channel_team.bind('conversations.overview.updated', (data: InboxUpdatePusherPayload) => {
        this._inboxListUpdate.next(data);
        Logger.log(`Pusher service: Inbox list event received. uuid: ${data.uuid}`);
      });

      this.channel_team.bind('segment.updated', (data: { segment: any }) => {
        this._segmentUpdate.next(data.segment);
        Logger.log(`Pusher service: Segment update event received. uuid: ${data.segment?.uuid}`);
      });

      // push when the connection changes back to Connection too
      this.pusher.connection.bind('connected', () => {
        Logger.info('Pusher status changed to connected');
        this._inboxListUpdate.next(null);
      });
    }
  }

  private registerForKnowledgeBaseUpdates() {
    if (this.channel_team) {
      this.channel_team.bind('knowledge_hub.import.progress', (data: KnowledgeUploadProgress) => {
        this._knowledgeHubImportProgress.next(data);
        Logger.log(`Pusher service: Knowledge Hub import progress event received. id: ${data.source_id}`);
      });

      this.channel_team.bind('knowledge_hub.changed', (data: { property_id: number }) => {
        this._knowledgeHubChanged.next(data);
        Logger.log(`Pusher service: Knowledge Hub changed event received. id: ${data.property_id}`);
      });
    }
  }

  private registerForCopilotUpdates() {
    if (this.channel_user) {
      this.channel_user.bind('copilot.progress', (data: CopilotProgress) => {
        this._copilotProgress.next(data);
        Logger.log(`Pusher service: Copilot progress event received. Status: ${data.status}, Message: ${data.message}`);
      });
    }
  }

  public getConversationUpdatesForAThreadUuid(uuid: string): Observable<InboxUpdatePusherPayload> {
    return this.inboxListUpdate.pipe(
      filter((item) => item && 'uuid' in item),
      filter((item) => item.uuid === uuid)
    );
  }

  private registerForNotificationNew() {
    this.channel_global.bind('notification.new', (data) => {
      this.notificationsService.addNotification(data);
    });

    this.channel_user.bind('notification.new', (data) => {
      this.notificationsService.addNotification(data);
    });
  }

  private registerForNotificationUpdate() {
    this.channel_global.bind('notification.update', (data) => {
      this.notificationsService.updateNotification(data);
    });

    this.channel_user.bind('notification.update', (data) => {
      this.notificationsService.updateNotification(data);
    });
  }

  private registerForNotificationDelete() {
    this.channel_global.bind('notification.delete', (data) => {
      this.notificationsService.deleteNotification(data);
    });

    this.channel_user.bind('notification.delete', (data) => {
      this.notificationsService.deleteNotification(data);
    });
  }

  private registerForListingReviewUpdates() {
    this.channel_user.bind('channel.listings.update', (data) => {
      this._listingsUpdate.next(data);
    });
  }

  private registerForOnboardingUpdates() {
    this.channel_user.bind('onboarding.update', (data) => {
      this._onboardingUpdate.next(data);
    });
  }

  private registerForBookingComUpdates() {
    this.channel_user.bind('booking.com.update', (data) => {
      this._bookingComUpdate.next(data);
    });
  }

  private registerForAccountingConnectionUpdates() {
    if (!this.channel_team) {
      return;
    }

    this.channel_team.bind('connection.finished_onboarding', (data) => {
      this._accountingConnectionUpdate.next({ type: 'finished_onboarding', connection: data.connection });
    });

    this.channel_team.bind('connection.initial_sync_complete', (data) => {
      this._accountingConnectionUpdate.next({ type: 'initial_sync_complete', connection: data.connection });
    });

    this.channel_team.bind('connection.failed_onboarding', (data) => {
      this._accountingConnectionUpdate.next({ type: 'failed_onboarding', connection: data.connection });
    });

    this.channel_team.bind('connection.refreshed', (data) => {
      this._accountingConnectionUpdate.next({ type: 'refreshed', connection: data.connection });
    });
  }

  public getDynamicPricingRecommendationsUpdate(
    propertyId: number
  ): Observable<{ property_id: number; property_uuid: string; uid: number }> {
    return this.dynamicPricingRecommendationsUpdate$.pipe(filter((item) => item?.property_id === propertyId));
  }

  private registerForDynamicPricingRecommendationsUpdates() {
    if (!this.channel_team) {
      return;
    }

    this.channel_team.bind('dynamic-pricing.fetched-prices', (data) => {
      this._dynamicPricingRecommendationsUpdate.next(data);
    });
  }

  private registerForSubscriptionUpdates() {
    if (!this.channel_team) {
      return;
    }

    this.channel_team.bind('subscription.entitlements.updated', () => {
      this._subscriptionUpdate.next();
    });
  }

  private registerForTaskUpdates() {
    if (!this.channel_team) {
      return;
    }

    this.channel_team.bind('task.update', (data: TaskInstanceExtended) => {
      this._taskUpdate.next(data);
      Logger.log(`Pusher service: Task update event received. uuid: ${data.uuid}`);
    });

    this.channel_team.bind('task.message.new', (data: TaskMessage) => {
      this._taskMessageUpdate.next(data);
      Logger.log(`Pusher service: Task message new event received. uuid: ${data.id}`);
    });
  }

  public getTaskUpdatesForUuid(uuid: string): Observable<TaskInstanceExtended> {
    return this.taskUpdate$.pipe(filter((task) => task.uuid === uuid));
  }

  private registerForUpsellsUpdates() {
    if (!this.channel_team) {
      return;
    }

    this.channel_team.bind('reservationUpsells.update', (data: { reservation_uuid: string }) => {
      this._reservationUpsellsUpdate.next(data);
      Logger.log(`Pusher service: Reservation upsells update event received. Reservation UUID: ${data.reservation_uuid}`);
    });
  }
}
